import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LoginService } from '../../login/login.service';
import { PermissionType } from '../../admin/roles/roles.model';

export interface BadgeItem {
  type: string;
  value: string;
}
export interface Saperator {
  name: string;
  type?: string;
}
export interface SubChildren {
  state: string;
  name: string;
  type?: string;
}
export interface ChildrenItems {
  state: string;
  name: string;
  type?: string;
  child?: SubChildren[];
  requiredPermissions?: string[];
}

export interface Menu {
  state: string;
  name: string;
  type: string;
  icon: string;
  badge?: BadgeItem[];
  saperator?: Saperator[];
  children?: ChildrenItems[];
  indicator?: boolean;
  requiredPermissions?: string[];
}

const MENUITEMS = [
  {
    state: 'panel',
    name: 'Panel',
    type: 'link',
    icon: 'developer_board',
    requiredPermissions: [
      PermissionType.AUCTION_FULL, 
      PermissionType.AUCTION_READ, 
      PermissionType.AUCTION_WRITE, 
      PermissionType.AUCTION_PANEL,
      PermissionType.NOTIFICATIONS_WRITE,
      PermissionType.BID_CUT
    ],
  },
  {
    state: 'hammer',
    name: 'Martillo',
    type: 'link',
    icon: 'gavel',
    requiredPermissions: [PermissionType.HAMMER_FULL, PermissionType.HAMMER_READ, PermissionType.BID_CUT],
  },
  {
    state: 'users',
    name: 'Usuarios',
    type: 'sub',
    icon: 'account_box',
    children: [
      { state: 'list', name: 'Listado', type: 'link' },
      { state: 'import', name: 'Importar', type: 'link' },
    ],
    indicator: true,
    requiredPermissions: [
      PermissionType.USER_FULL,
      PermissionType.USER_READ,
      PermissionType.USER_WRITE,
      PermissionType.USER_DELETE,
      PermissionType.USER_UPDATE,
    ],
  },
  {
    state: 'lots',
    name: 'Lotes',
    type: 'sub',
    icon: 'content_copy',
    children: [
      { state: 'list', name: 'Listado', type: 'link' },
      { state: 'import', name: 'Importar', type: 'link' },
    ],
    requiredPermissions: [
      PermissionType.LOT_FULL,
      PermissionType.LOT_READ,
      PermissionType.LOT_WRITE,
      PermissionType.LOT_DELETE,
      PermissionType.LOT_UPDATE,
    ],
  },
  {
    state: 'reports',
    name: 'Reportes',
    type: 'link',
    icon: 'assessment',
    requiredPermissions: [
      PermissionType.REPORTS_FULL,
      PermissionType.REPORTS_READ
    ],
  },
  {
    state: 'marketing',
    name: 'Publicidad',
    type: 'link',
    icon: 'assessment',
    requiredPermissions: [
      PermissionType.MARKETING_FULL,
      PermissionType.MARKETING_READ,
      PermissionType.MARKETING_WRITE,
      PermissionType.MARKETING_DELETE,
      PermissionType.MARKETING_UPDATE,
    ],
  },
  {
    state: 'config',
    name: 'Configuración',
    type: 'link',
    icon: 'settings',
    requiredPermissions: [
      PermissionType.CONFIG_FULL,
      PermissionType.CONFIG_READ,
      PermissionType.CONFIG_WRITE,
      PermissionType.CONFIG_UPDATE,
    ],
  },
  {
    state: 'admin',
    name: 'Admin',
    type: 'sub',
    icon: 'admin_panel_settings',
    children: [
      { state: 'users', 
        name: 'Usuarios', 
        type: 'link', 
        requiredPermissions: [
          PermissionType.SYSTEM_USERS_FULL, 
          PermissionType.SYSTEM_USERS_READ, 
          PermissionType.SYSTEM_USERS_WRITE, 
          PermissionType.SYSTEM_USERS_DELETE,
          PermissionType.SYSTEM_USERS_UPDATE,
        ] 
      },
      { state: 'roles', 
        name: 'Roles', 
        type: 'link', 
        requiredPermissions: [
          PermissionType.ROLE_FULL, 
          PermissionType.ROLE_READ, 
          PermissionType.ROLE_WRITE, 
          PermissionType.ROLE_DELETE,
          PermissionType.ROLE_UPDATE,
        ] 
      },
      { state: 'audits', 
        name: 'Auditoría', 
        type: 'link', 
        requiredPermissions: [
          PermissionType.AUDIT_READ
        ] 
      },
    ]
  },
];

@Injectable()
export class MenuItems {
  constructor(private readonly loginService: LoginService) {}

  /**
   * Reactive stream of filtered menu items based on current user's permissions
   */
  public readonly filteredMenuItems$: Observable<Menu[]> = this.loginService.currentUser$.pipe(
    map(user => {
      if (!user) {
        return [];
      }
      return MENUITEMS.filter(menuItem => this.isMenuItemVisible(menuItem)).map(menuItem =>
        this.filterMenuItemWithChildren(menuItem)
      );
    })
  );

  private isMenuItemVisible(menuItem: Menu): boolean {
    const hasParentPermission = this.hasRequiredPermissions(menuItem);
    if (!menuItem.children || menuItem.children.length === 0) {
      return hasParentPermission;
    }
    const hasVisibleChild = menuItem.children.some(child => this.hasRequiredPermissions(child));
    if (!menuItem.requiredPermissions?.length) {
      return hasVisibleChild;
    }
    return hasParentPermission && hasVisibleChild;
  }

  private filterMenuItemWithChildren(menuItem: Menu): Menu {
    if (!menuItem.children || menuItem.children.length === 0) {
      return menuItem;
    }
    const filteredChildren = menuItem.children.filter(child => this.hasRequiredPermissions(child));
    return { ...menuItem, children: filteredChildren };
  }

  private hasRequiredPermissions(menuItem: Menu | ChildrenItems): boolean {
    if (!menuItem.requiredPermissions || menuItem.requiredPermissions.length === 0) {
      return true;
    }
    return this.loginService.hasAnyPermission(menuItem.requiredPermissions);
  }
}
